import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, defer, filter, retry, switchMap, take } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { NotificationApiService } from './notification-api.service';
import { INotification } from '../interfaces/notification.interface';

@Injectable({ providedIn: 'root' })
export class InboxService {
	private readonly openSubject = new BehaviorSubject<boolean>(false);
	private readonly unreadCountSubject = new BehaviorSubject<number>(0);

	readonly isOpen$ = this.openSubject.asObservable();
	readonly unreadCount$ = this.unreadCountSubject.asObservable();
	readonly newNotification$ = new Subject<INotification>();

	constructor(
		private readonly auth: AuthService,
		private readonly notificationApiService: NotificationApiService,
	) {
		this.auth.isAuthenticated$.pipe(
			filter(isAuth => isAuth),
			take(1),
			switchMap(() => defer(() => {
				this.refreshUnreadCount();
				return this.notificationApiService.stream();
			})),
			// EventSource fires onerror on any transient drop; without this the stream
			// would end permanently and real-time notifications would stop. Reconnect.
			retry({ delay: 3000 }),
		).subscribe({
			next: (notification) => {
				this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
				this.newNotification$.next(notification);
			},
		});
	}

	toggle(): void { this.openSubject.next(!this.openSubject.value); }
	close(): void { this.openSubject.next(false); }
	get isOpen(): boolean { return this.openSubject.value; }

	setUnreadCount(count: number): void { this.unreadCountSubject.next(count); }
	incrementUnread(): void { this.unreadCountSubject.next(this.unreadCountSubject.value + 1); }

	/**
	 * Re-reads the unread total from the server and pushes it to the badge. Used to seed the badge
	 * at session start and after any action that changes what is unread (read one, read all,
	 * archive all), so the counter never depends on the inbox panel having been opened.
	 */
	refreshUnreadCount(): void {
		this.notificationApiService.unreadCount().subscribe({
			next: count => this.unreadCountSubject.next(count),
			error: () => false,
		});
	}
}
